// The vote balance surface: the sheet's own arithmetic, rendered the way a
// presiding officer checks it by hand before signing.
//
// Presentational only — every jump is a callback, so these render identically
// in the sticky footer, the summary step and Preview.

import { fmt, signed } from '../../utils/number';
import { AlertSVG, CheckSVG } from '../../utils/svgs';
import type { IChildBalance, ISheetCheck, IVoteBalance } from './vote-balance';

/** CSS tone suffix, matching the reconciliation module's ok/minor/critical/locked. */
export const balanceTone = (b: IVoteBalance): string => {
	if (b.state === 'notStarted') return 'locked';
	if (b.state === 'awaitingCoverage') return 'locked';
	if (b.severity === 'critical') return 'critical';
	if (b.severity === 'minor') return 'minor';
	return 'ok';
};

/** One sentence stating where the count stands. Never apologises, never hedges. */
export const balanceHeadline = (b: IVoteBalance): string => {
	switch (b.state) {
		case 'notStarted':
			return 'Enter Valid Votes and the party scores to check them against each other.';
		case 'awaitingCoverage':
			return `${fmt(b.coverage?.recorded ?? 0)} of ${fmt(b.coverage?.total ?? 0)} units recorded.`;
		case 'balanced':
			return b.hasSheetIssue
				? 'Party votes match, but the ballot account does not close.'
				: 'Party votes match the result sheet.';
		default:
			// "short of" takes the preposition; "over" does not.
			return `Party votes are ${fmt(Math.abs(b.difference))} ${b.direction === 'short' ? 'short of' : 'over'} Valid Votes.`;
	}
};

/**
 * THE SIGNATURE — the balance rail.
 *
 * Deliberately not a progress bar. Progress says "more is better, 100% is done";
 * here an overshoot is exactly as wrong as a shortfall. The declared figure is a
 * fixed notch; the count fills toward it and either lands on it, stops short, or
 * carries on past it as a separate over-segment.
 */
export const VoteBalanceRail = ({ balance }: { balance: IVoteBalance }) => {
	const { declared, counted, difference, state } = balance;
	const tone = balanceTone(balance);
	// The notch sits at a fixed 78% so there is always room to render an overshoot.
	const NOTCH = 78;
	const scale = declared > 0 ? NOTCH / declared : 0;
	const fill = Math.min(counted * scale, NOTCH);
	const over = Math.min(Math.max(counted - declared, 0) * scale, 100 - NOTCH);

	return (
		<div className={`vb-rail vb-rail--${tone}`}>
			<div className='vb-rail__heads'>
				<span className='vb-rail__head'>
					<span className='vb-rail__head-label'>Counted</span>
					<span className='vb-rail__head-value'>{fmt(counted)}</span>
				</span>
				<span className='vb-rail__head vb-rail__head--end'>
					<span className='vb-rail__head-label'>Declared</span>
					<span className='vb-rail__head-value'>{fmt(declared)}</span>
				</span>
			</div>
			<div
				className='vb-rail__track'
				role='img'
				aria-label={`${fmt(counted)} counted against ${fmt(declared)} declared, ${signed(difference)}`}
			>
				<span
					className='vb-rail__fill'
					style={{ width: `${fill}%` }}
				/>
				{over > 0 && (
					<span
						className='vb-rail__over'
						style={{ left: `${NOTCH}%`, width: `${over}%` }}
					/>
				)}
				<span
					className='vb-rail__notch'
					style={{ left: `${NOTCH}%` }}
				/>
			</div>
			<p className={`vb-rail__delta vb-rail__delta--${tone}`}>
				{state === 'balanced' ? 'Balanced' : state === 'off' ? `${signed(difference)} ${balance.direction}` : '—'}
			</p>
		</div>
	);
};

/** Compact one-liner. Fits the sticky footer, the summary step and Preview. */
export const VoteBalanceStrip = ({
	balance,
	onReview,
	reviewLabel = 'Review',
}: {
	balance: IVoteBalance;
	onReview?: () => void;
	reviewLabel?: string;
}) => {
	if (balance.state === 'notStarted' && !balance.hasSheetIssue) return null;
	const tone = balanceTone(balance);
	const settled = balance.state === 'balanced' && !balance.hasSheetIssue;

	return (
		<div className={`vb-strip vb-strip--${tone}`}>
			<span className='vb-strip__icon'>
				{settled ? (
					<CheckSVG
						width='12'
						height='12'
						color='currentColor'
					/>
				) : (
					<AlertSVG />
				)}
			</span>
			<span className='vb-strip__detail'>
				{balanceHeadline(balance)}
				{balance.state !== 'notStarted' && (
					<span className='vb-strip__figures'>
						{fmt(balance.counted)} counted · {fmt(balance.declared)} declared
					</span>
				)}
			</span>
			{onReview && (
				<button
					type='button'
					className='vb-strip__action'
					onClick={onReview}
				>
					{reviewLabel}
				</button>
			)}
		</div>
	);
};

const SheetCheckRow = ({ check, onFix }: { check: ISheetCheck; onFix?: (c: ISheetCheck) => void }) => (
	<li className={`vb-check ${check.ok ? 'vb-check--ok' : 'vb-check--off'}`}>
		<span className='vb-check__mark'>{check.ok ? '✓' : '✗'}</span>
		<span className='vb-check__body'>
			<span className='vb-check__label'>{check.label}</span>
			<span className='vb-check__expr'>{check.expression}</span>
		</span>
		{!check.ok && onFix && (
			<button
				type='button'
				className='vb-check__fix'
				onClick={() => onFix(check)}
			>
				Fix
			</button>
		)}
	</li>
);

const ChildRow = ({ child, onOpen }: { child: IChildBalance; onOpen?: (c: IChildBalance) => void }) => (
	<button
		type='button'
		className={`vb-child ${child.recorded ? '' : 'vb-child--pending'}`}
		onClick={() => onOpen?.(child)}
		disabled={!onOpen}
	>
		<span className='vb-child__code'>{child.code}</span>
		<span className='vb-child__name'>{child.name}</span>
		<span className='vb-child__value'>{child.recorded ? fmt(child.subtotal) : 'Not recorded'}</span>
	</button>
);

/**
 * The full ledger. Two groups, because those are the two boxes on the paper the
 * agent is holding: the ballot account, and the party count.
 */
export const VoteBalancePanel = ({
	balance,
	childLabel = 'units',
	onCheckValidVotes,
	onReviewPartyVotes,
	onFixCheck,
	onOpenChild,
}: {
	balance: IVoteBalance;
	childLabel?: string;
	onCheckValidVotes?: () => void;
	onReviewPartyVotes?: () => void;
	onFixCheck?: (c: ISheetCheck) => void;
	onOpenChild?: (c: IChildBalance) => void;
}) => {
	const tone = balanceTone(balance);
	const showActions = balance.state === 'off' || balance.hasSheetIssue;

	return (
		<section className={`vb-panel vb-panel--${tone}`}>
			<header className='vb-panel__head'>
				<p className='vb-panel__eyebrow'>Result sheet check</p>
				<h4 className='vb-panel__verdict'>{balanceHeadline(balance)}</h4>
			</header>

			{balance.checks.length > 0 && (
				<div className='vb-group'>
					<p className='vb-group__title'>Ballot account</p>
					{balance.hasSheetIssue ? (
						<ul className='vb-group__list'>
							{balance.checks.map((c) => (
								<SheetCheckRow
									key={c.key}
									check={c}
									onFix={onFixCheck}
								/>
							))}
						</ul>
					) : (
						// Every line closes — say so once. Itemising four passing
						// equations buries the one number the agent came here for.
						<p className='vb-closed'>
							<span className='vb-closed__mark'>✓</span>
							The ballot account closes on all {balance.checks.length} counts.
						</p>
					)}
				</div>
			)}

			<div className='vb-group'>
				<p className='vb-group__title'>Party count</p>
				<VoteBalanceRail balance={balance} />
			</div>

			{balance.children.length > 0 && (
				<div className='vb-group'>
					<p className='vb-group__title'>
						By {childLabel}
						{balance.coverage && (
							<span className='vb-group__coverage'>
								{fmt(balance.coverage.recorded)} of {fmt(balance.coverage.total)} recorded
							</span>
						)}
					</p>
					<div className='vb-group__children'>
						{balance.children.map((c) => (
							<ChildRow
								key={c.id}
								child={c}
								onOpen={onOpenChild}
							/>
						))}
						<div className='vb-child vb-child--total'>
							<span className='vb-child__code' />
							<span className='vb-child__name'>All {childLabel}</span>
							<span className='vb-child__value'>{fmt(balance.counted)}</span>
						</div>
					</div>
				</div>
			)}

			{showActions && (
				<div className='vb-panel__actions'>
					{onCheckValidVotes && (
						<button
							type='button'
							className='vb-panel__action'
							onClick={onCheckValidVotes}
						>
							Check Valid Votes
						</button>
					)}
					{onReviewPartyVotes && (
						<button
							type='button'
							className='vb-panel__action'
							onClick={onReviewPartyVotes}
						>
							Review party votes
						</button>
					)}
				</div>
			)}
		</section>
	);
};
